import { useCallback, useState } from 'react'

export type DashboardMode = 'user' | 'admin'

const STORAGE_KEY = 'ytclip.dashboardMode'

function readStoredMode(): DashboardMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'admin' ? 'admin' : 'user'
  } catch {
    return 'user'
  }
}

/**
 * Which face of the dashboard a super admin is looking at.
 *
 * A super admin has an ordinary account as well — balance, API keys, usage —
 * so this is a view toggle, not impersonation: admin mode shows the settings
 * and user management panels, user mode shows their own dashboard. No request
 * is made as anyone else.
 *
 * Non-admins are pinned to user mode regardless of what is in storage.
 */
export function useDashboardMode(isSuperAdmin: boolean) {
  const [stored, setStored] = useState<DashboardMode>(readStoredMode)

  const mode: DashboardMode = isSuperAdmin ? stored : 'user'

  const setMode = useCallback((next: DashboardMode) => {
    setStored(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Private browsing or blocked storage; the toggle still works for this session.
    }
  }, [])

  const toggle = useCallback(() => {
    setMode(mode === 'admin' ? 'user' : 'admin')
  }, [mode, setMode])

  return { mode, setMode, toggle, canUseAdminMode: isSuperAdmin }
}
